import { useState } from 'react'
import { GREY_300, GREY_400, GREY_500, WHITE, primaryButtonStyle } from '@/lib/genesis'

/**
 * Show-once modal for a newly minted API key's plaintext value (ADR-119
 * D3 / task brief step 5). Once `onClose` fires, the caller MUST discard
 * the plaintext from its own state -- this component itself holds no copy
 * beyond the `plaintextKey` prop and never re-fetches or re-displays it;
 * there is no "show again" affordance anywhere, matching the backend's own
 * guarantee (CreateApiKeyResponse.plaintext_key is the only RPC response
 * that ever carries key material, per ADR-119 D3).
 *
 * Three-surface UI alignment pass (three-surface-ui-alignment): restyled
 * onto Genesis tokens + glass-card idiom; behavior/test-id unchanged.
 */
export function ShowOnceKeyModal({
  plaintextKey,
  onClose,
}: {
  plaintextKey: string
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="glass-card w-full max-w-lg p-5">
        <h2 className="font-sub mb-2 text-base font-semibold" style={{ color: WHITE }}>
          New API key created
        </h2>
        <p className="mb-3 text-sm" style={{ color: GREY_400 }}>
          This is the only time this key will ever be shown. Copy it now —
          Console never stores or displays the plaintext again.
        </p>
        <div className="mb-3 flex items-center gap-2 rounded-lg p-2" style={{ border: `1px solid ${GREY_500}`, backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
          <code
            data-testid="plaintext-key-value"
            className="flex-1 overflow-x-auto whitespace-nowrap font-mono text-sm"
            style={{ color: WHITE }}
          >
            {plaintextKey}
          </code>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(plaintextKey)
              setCopied(true)
            }}
            className="glass-button shrink-0 rounded-md px-2 py-1 text-xs"
            style={{ color: GREY_300 }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-lg py-2 text-sm font-semibold"
          style={primaryButtonStyle}
        >
          I've saved it — close
        </button>
      </div>
    </div>
  )
}
